/**
 * Everything in the console, one keystroke away.
 *
 * WHY THIS EXISTS AT ALL. The navigation folds sixteen of its twenty-seven
 * destinations behind Setup, because configuration is somewhere you go a few
 * times and then leave, and left open it pushed the nav past the height of the
 * window. Hiding things is only honest if there is a faster way to reach them
 * than the list you took them out of. This is that way, and the two changes
 * belong together: shortening the nav without building this would have been
 * making the app tidier at the operator's expense.
 *
 * It searches two things, and they are different in kind. DESTINATIONS are a
 * fixed list this file already holds, so they filter as you type with no
 * request at all. PEOPLE are a query, so they are debounced and only asked for
 * once there is something worth asking about. Keeping both in one list is the
 * point: somebody typing a name should not have to have decided in advance
 * whether they were looking for a page or a person.
 *
 * WHAT IT DELIBERATELY IS NOT. It runs no actions on anybody's records. A
 * palette that can approve, send or delete is a palette where a mistyped letter
 * and a stray Enter do something to somebody, and this product's destructive
 * steps are the ones where that matters most. EVERYTHING here navigates, with
 * no exceptions, so the worst a wrong guess costs is a page you did not want.
 *
 * It used to have one: "Switch to dark mode" and the two rows beside it, which
 * set a preference rather than going anywhere. They went with the dark palette
 * (the owner, 2026-10-06: "remove the dark mode from the app and the auto theme
 * selection based on os"), and losing them is what makes the sentence above
 * true without a caveat. Every row is a destination again, so `choose` has one
 * branch and nothing here reads or writes a setting.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '../lib/cn'
import { useDebouncedValue } from '../lib/hooks'
import { useSession } from '../lib/session'
import { api } from '../lib/api'
import { useQuery } from '@tanstack/react-query'
import { HANDBOOK_ROW, NAV_GROUPS, OPERATOR_GLYPHS, PLATFORM_ITEMS, PROSPECTING_GLYPHS, SETTINGS_GROUPS } from './Sidebar'
import {
  OPERATOR_HEADING,
  OPERATOR_TASK_LINKS,
  OPERATOR_UNLISTED,
  PROSPECTING_HEADING,
  PROSPECTING_LINKS,
  PROSPECTING_UNLISTED,
  mayOpen,
  operatorLinksFor,
  type OperatorOffer,
  type ProspectingLink,
} from './sidebar-model'
import { IconTarget } from '../pages/prospecting/shared/icons'
import { useProspectingSeat } from '../pages/prospecting/shared/useProspectingSeat'
import { useOperatorReach } from '../pages/operator/shared/useOperatorMe'
import { IconSearch, IconProspects, IconVideo } from './icons'
import type { ComponentType } from 'react'
import type { IconProps } from './icons'
import type { Paginated, ProspectListItem } from '../lib/types'

export interface Choice {
  id: string
  label: string
  hint?: string
  group: string
  to: string
  icon: ComponentType<IconProps>
  /**
   * Other names the destination goes by, matched as its label is. A sub-row
   * that opens its row's own page (Creators opens Audience) is offered once,
   * under the row, and found by its own name through this.
   */
  also?: readonly string[]
}

/**
 * A match, and how good a one.
 *
 * Three tiers rather than a score, because the difference that matters to
 * somebody typing is whether the thing they meant is at the TOP, and a
 * continuous score spreads near-ties into an order nobody can predict. A label
 * that starts with what you typed beats a label that merely contains it, which
 * beats a match found only in the description. Within a tier the original order
 * is kept, so the list does not reshuffle itself under the cursor.
 */
export function rank(choice: Pick<Choice, 'label' | 'group' | 'hint' | 'also'>, needle: string): number | null {
  if (needle === '') return 3
  const names = [choice.label, ...(choice.also ?? [])].map((name) => name.toLowerCase())
  if (names.some((name) => name.startsWith(needle))) return 0
  if (names.some((name) => name.includes(needle))) return 1
  if (`${choice.group} ${choice.hint ?? ''}`.toLowerCase().includes(needle)) return 2
  return null
}

/**
 * One choice per destination, the first one kept.
 *
 * Device and the Activity log have a row in the Operations group and a place in
 * the settings rail, so both lists offer them. The nav's entry comes first and
 * wins, and the second would otherwise be a duplicate result and a duplicate
 * React key.
 */
export function dedupeByPath(choices: readonly Choice[]): Choice[] {
  const seen = new Set<string>()
  const out: Choice[] = []
  for (const choice of choices) {
    if (seen.has(choice.to)) continue
    seen.add(choice.to)
    out.push(choice)
  }
  return out
}

/*
 * BOTH LISTS, AND THE SECOND ONE IS NOW THE REASON THIS CONTROL MATTERS.
 *
 * The thirteen settings screens came off the sidebar when they moved behind a
 * single Settings row. The sidebar is shorter for it and every one of those
 * screens is two clicks away instead of one. This is what puts them back to
 * one keystroke, so a person who knows they want Suppressions types it rather
 * than opening Settings and reading a rail.
 *
 * They are prefixed in the group label rather than merged into it, because
 * "Sending" on its own would sit in these results next to Today and Pipeline
 * looking like a fourth part of the navigation, which it is not.
 */
export function paletteDestinations(isAdmin: boolean, superAdminView: boolean, prospecting = false, operator: OperatorOffer | null = null): Choice[] {
  return dedupeByPath([
    ...NAV_GROUPS.flatMap((group) =>
      group.items
        .filter((item) => mayOpen(item, isAdmin, superAdminView))
        .map((item) => ({
          id: `nav:${item.to}`,
          label: item.label,
          hint: item.hint,
          group: group.heading,
          to: item.to,
          icon: item.icon,
        })),
    ),
    // The prospects pages, for an admin or anybody with a seat there: every
    // row the column draws and every page under one, under the column's own
    // heading. Router links like the rest since they became this app's pages.
    ...(prospecting ? prospectingDestinations() : []),
    // The Operator pages, for an admin or anybody with a seat there
    // (`useOperatorReach`), under the module's heading. Talk only for whom it
    // may open, the sandbox and the admin page only for an Operator admin.
    ...(operator?.seat === true ? operatorDestinations(operator) : []),
    // The task engine's pages (Companies), for an admin whatever the meetings
    // service says, because they need no seat there. Deduped against the above.
    ...operatorTaskDestinations(isAdmin),
    // A settings screen marked `operator` (Settings > Operator) only for an Operator admin, as its tab is.
    ...SETTINGS_GROUPS.flatMap((group) =>
      group.items
        .filter((item) => mayOpen(item, isAdmin, superAdminView))
        .filter((item) => item.operator !== true || operator?.admin === true)
        .map((item) => ({
          id: `nav:${item.to}`,
          label: item.label,
          hint: item.hint,
          group: `Settings: ${group.heading.toLowerCase()}`,
          to: item.to,
          icon: item.icon,
        })),
    ),
    // The handbook has a row at the foot of the column rather than a place in
    // a group, so it is named here on its own. It was found through the
    // settings list until it moved to `/handbook`.
    {
      id: `nav:${HANDBOOK_ROW.to}`,
      label: HANDBOOK_ROW.label,
      hint: HANDBOOK_ROW.hint,
      group: 'Help',
      to: HANDBOOK_ROW.to,
      icon: HANDBOOK_ROW.icon,
    },
    // The Prospecting settings for a member with a seat, who has no
    // administration group to find them in. An admin's comes from that group
    // above and wins the dedupe.
    ...(prospecting
      ? [
          {
            id: 'nav:/settings/prospecting',
            label: 'Prospecting settings',
            hint: 'Your voice, your feed and your brands, and the writing schedule for owners',
            group: 'Settings: prospecting',
            to: '/settings/prospecting',
            icon: IconTarget,
          },
        ]
      : []),
    // The Operator settings for a member who is an Operator admin, the same
    // way (they are for admins only). A GojiB admin's comes from the
    // administration group and wins the dedupe.
    ...(operator?.seat === true && operator.admin
      ? [
          {
            id: 'nav:/settings/operator',
            label: 'Operator settings',
            hint: 'The clone and its prompts, models, voice and face, and the meetings it joins',
            group: 'Settings: operator',
            to: '/settings/operator',
            icon: IconVideo,
          },
        ]
      : []),
    // The every-brand screens live behind the view switcher rather than in
    // the nav now, and are offered here on the same terms as before: only
    // to somebody in the super admin view.
    ...PLATFORM_ITEMS.filter((item) => mayOpen(item, isAdmin, superAdminView)).map((item) => ({
      id: `nav:${item.to}`,
      label: item.label,
      hint: item.hint,
      group: 'Platform',
      to: item.to,
      icon: item.icon,
    })),
  ])
}

/**
 * The Prospecting rows and the pages under them, one choice each. A row whose
 * first page is its own (Audience opens Creators) is offered once, under the
 * row's name and found by the sub-row's name too, and its other pages by
 * their own names. Then the pages no row draws (`PROSPECTING_UNLISTED`): One
 * at a time, the two searches and the browser helper's setup.
 */
export function prospectingDestinations(): Choice[] {
  return [
    ...PROSPECTING_LINKS.flatMap((link) => prospectingRowDestinations(link)),
    ...PROSPECTING_UNLISTED.map((page) => ({
      id: `nav:${page.href}`,
      label: page.label,
      hint: page.hint,
      group: page.settings === true ? 'Settings: prospecting' : PROSPECTING_HEADING,
      to: page.href,
      icon: PROSPECTING_GLYPHS[page.glyph],
    })),
  ]
}

/**
 * The Operator rows this person sees (Talk only when it may open), then the
 * pages no row draws (`OPERATOR_UNLISTED`): the clone's meeting sign-in,
 * preferences, onboarding, and for an Operator admin the sandbox and the
 * admin page of Settings > Operator.
 */
export function operatorDestinations(offer: Pick<OperatorOffer, 'admin' | 'talk'>): Choice[] {
  return [
    ...operatorLinksFor({ seat: true, talk: offer.talk }).map((link) => ({
      id: `nav:${link.href}`,
      label: link.label,
      hint: link.hint,
      group: OPERATOR_HEADING,
      to: link.href,
      icon: OPERATOR_GLYPHS[link.glyph],
    })),
    ...OPERATOR_UNLISTED.filter((page) => page.adminOnly !== true || offer.admin).map((page) => ({
      id: `nav:${page.href}`,
      label: page.label,
      hint: page.hint,
      group: page.settings === true ? 'Settings: operator' : OPERATOR_HEADING,
      to: page.href,
      icon: OPERATOR_GLYPHS[page.glyph],
    })),
  ]
}

/** The Operator task engine's rows (Companies), for admins only, as their routes. */
export function operatorTaskDestinations(isAdmin: boolean): Choice[] {
  if (!isAdmin) return []
  return OPERATOR_TASK_LINKS.map((link) => ({
    id: `nav:${link.href}`,
    label: link.label,
    hint: link.hint,
    group: OPERATOR_HEADING,
    to: link.href,
    icon: OPERATOR_GLYPHS[link.glyph],
  }))
}

function prospectingRowDestinations(link: ProspectingLink): Choice[] {
  const same = (link.children ?? []).filter((child) => child.href === link.href).map((child) => child.label)
  return [
    {
      id: `nav:${link.href}`,
      label: link.label,
      hint: link.hint,
      group: PROSPECTING_HEADING,
      to: link.href,
      icon: PROSPECTING_GLYPHS[link.glyph],
      ...(same.length === 0 ? {} : { also: same }),
    },
    ...(link.children ?? [])
      .filter((child) => child.href !== link.href)
      .map((child) => ({
        id: `nav:${child.href}`,
        label: child.label,
        hint: `${link.label}: ${link.hint}`,
        group: PROSPECTING_HEADING,
        to: child.href,
        icon: PROSPECTING_GLYPHS[link.glyph],
      })),
  ]
}

/**
 * A row in the results. Every one is a destination.
 *
 * Kept as a name of its own rather than collapsed into `Choice`, because the
 * rows come from three places (the navigation, the settings groups and a people
 * query) and this alias is what says they are one kind of thing to this file.
 */
export type PaletteEntry = Choice

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const { isAdmin, superAdminView } = useSession()
  const prospecting = useProspectingSeat()
  const reach = useOperatorReach()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)

  // Reopening should not resume somebody else's half-typed search.
  useEffect(() => {
    if (open) {
      setQuery('')
      setActive(0)
    }
  }, [open])

  const needle = query.trim().toLowerCase()
  const debounced = useDebouncedValue(needle, 250)

  const destinations = useMemo<PaletteEntry[]>(
    () => paletteDestinations(isAdmin, superAdminView, prospecting, { seat: reach.seat, admin: reach.admin, talk: reach.talk }),
    [isAdmin, superAdminView, prospecting, reach.seat, reach.admin, reach.talk],
  )

  const matches = useMemo(() => {
    const scored: { choice: PaletteEntry; tier: number }[] = []
    for (const choice of destinations) {
      const tier = rank(choice, needle)
      if (tier !== null) scored.push({ choice, tier })
    }
    // A stable sort, so equal tiers keep the order the navigation puts them in.
    return scored.sort((a, b) => a.tier - b.tier).map((entry) => entry.choice)
  }, [destinations, needle])

  /*
   * Two characters before asking. One letter matches most of the database and
   * the answer is never what somebody wanted, so the request is pure cost.
   */
  const peopleQuery = useQuery({
    queryKey: ['palette-people', debounced],
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
    queryFn: ({ signal }) =>
      api.get<Paginated<ProspectListItem>>('/prospects', {
        params: { q: debounced, limit: 5 },
        signal,
      }),
  })

  const people: PaletteEntry[] = (peopleQuery.data?.items ?? []).map((person) => ({
    id: `person:${person.id}`,
    label: person.fullName,
    hint: person.company ?? undefined,
    group: 'People',
    to: `/prospects/${person.id}`,
    icon: IconProspects,
  }))

  const results = [...matches, ...people]
  // The cursor can outrun the list when a keystroke narrows it.
  const cursor = Math.min(active, Math.max(0, results.length - 1))

  useEffect(() => {
    const node = listRef.current?.querySelector('[data-active="true"]')
    if (node instanceof HTMLElement) node.scrollIntoView({ block: 'nearest' })
  }, [cursor, results.length])

  if (!open) return null

  const choose = (choice: PaletteEntry | undefined) => {
    if (choice === undefined) return
    onClose()
    navigate(choice.to)
  }

  return (
    <div className="fixed inset-0 z-modal" role="presentation">
      <div className="absolute inset-0 animate-fade-in bg-black/50" onClick={onClose} />
      {/*
        Held near the top rather than centred. A list that grows downward from a
        fixed point keeps the first result under the eye that is already reading
        the field, where a centred box moves every result each time the count
        changes.
      */}
      <div className="relative mx-auto mt-[12vh] w-[min(38rem,calc(100vw-2rem))]">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Search the console"
          className="overflow-hidden rounded-card border border-line bg-surface-raised shadow-pop"
        >
          <div className="flex items-center gap-2.5 border-b border-line px-3.5">
            <IconSearch aria-hidden className="shrink-0 text-sm text-ink-subtle" />
            <input
              ref={inputRef}
              autoFocus
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setActive(0)
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' || (event.key === 'n' && event.ctrlKey)) {
                  event.preventDefault()
                  setActive((was) => (results.length === 0 ? 0 : (was + 1) % results.length))
                } else if (event.key === 'ArrowUp' || (event.key === 'p' && event.ctrlKey)) {
                  event.preventDefault()
                  setActive((was) =>
                    results.length === 0 ? 0 : (was - 1 + results.length) % results.length,
                  )
                } else if (event.key === 'Enter') {
                  event.preventDefault()
                  choose(results[cursor])
                } else if (event.key === 'Escape') {
                  event.preventDefault()
                  onClose()
                }
              }}
              placeholder="Go to a page, or find someone"
              aria-label="Search the console"
              className="w-full bg-transparent py-3 text-sm text-ink outline-none placeholder:text-ink-faint"
            />
          </div>

          <ul ref={listRef} className="max-h-[22rem] overflow-y-auto p-1.5">
            {results.length === 0 ? (
              <li className="px-3 py-8 text-center text-sm text-ink-muted">
                {peopleQuery.isFetching ? 'Looking' : `Nothing matches ${query}`}
              </li>
            ) : (
              results.map((choice, index) => {
                /*
                 * The group heading is printed on the first row that carries it
                 * rather than by building a nested list, because the rows are
                 * ranked ACROSS groups: a heading is a label on a run, and the
                 * runs are whatever the ranking produced.
                 */
                const startsGroup = index === 0 || results[index - 1]?.group !== choice.group
                const isActive = index === cursor
                return (
                  <li key={choice.id}>
                    {startsGroup ? (
                      <p className="px-2.5 pb-1 pt-2.5 text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                        {choice.group}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      data-active={isActive}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => choose(choice)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition',
                        isActive ? 'bg-accent-soft text-accent-text' : 'text-ink-muted',
                      )}
                    >
                      <choice.icon
                        className={cn('h-[1.05rem] w-[1.05rem] shrink-0', isActive && 'text-accent')}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ink">
                          {choice.label}
                        </span>
                        {choice.hint ? (
                          <span className="block truncate text-2xs normal-case tracking-normal text-ink-subtle">
                            {choice.hint}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                )
              })
            )}
          </ul>

          <div className="flex items-center gap-3 border-t border-line px-3.5 py-2 text-2xs text-ink-faint">
            <span>Move with the arrow keys</span>
            <span>Enter chooses</span>
            <span>Esc closes</span>
          </div>
        </div>
      </div>
    </div>
  )
}
